import type { PropertyName } from "@frui.ts/helpers";
import { bound } from "@frui.ts/helpers";
import { attachAutomaticValidator } from "@frui.ts/validation/dist";
import { Modules } from "config";
import type InternalTransfer from "entities/internalTransferResponse";
import type { interfaces } from "inversify";
import { computed } from "mobx";
import InternalTransferRepository from "repositories/internalTransferRepository";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import SecurityService from "services/securityService";
import type { EntityValidationRules } from "services/validationService";
import EditableRowViewModel from "../../editableRowViewModel";

export type InternalTransferEditType = "messageForRecipient" | "amount";

export default class InternalTransferEditViewModel extends EditableRowViewModel<InternalTransfer> {
  readonly modelName = "internalTransfer";
  protected readonly validationRules: EntityValidationRules<InternalTransfer> = {
    amount: { range: { min: 0, allowEqual: false } },
    messageForRecipient: { isLength: { max: 30 } },
  };

  @computed
  get isEditable() {
    return (
      (!this.item.approvals || this.item.approvals.length === 0) && this.securityService.allowToEntity(this.modelName, "write")
    );
  }

  set editedProperty(property: PropertyName<InternalTransfer> | undefined) {
    this.setItem(this.originalItem);
    if (property) {
      const partialValidationRules = this.validationRules[property];
      if (partialValidationRules) {
        attachAutomaticValidator(this.item, { [property]: partialValidationRules });
      } else {
        attachAutomaticValidator(this.item, {});
      }
    }
    this._editedProperty = property;
  }

  @computed
  get editedProperty() {
    return this._editedProperty;
  }

  constructor(
    item: InternalTransfer,
    protected repository: InternalTransferRepository,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected notificationService: NotificationService,
    protected securityService: SecurityService
  ) {
    super(item);
  }

  @bound
  async updateData(): Promise<void> {
    await this.repository.update(this.item);
  }

  @bound
  translate(code: string, size: number | undefined): string {
    return this.localizationService.translateModule(Modules.finance, `internalTransfers.${code}`, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (item: InternalTransfer) =>
      new InternalTransferEditViewModel(
        item,
        container.get(InternalTransferRepository),
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(NotificationService),
        container.get(SecurityService)
      );
  }
}
